import React, {useState} from 'react';

import {
  AntibodyTagSchemaQuery,
  AntibodyTagSchemaQueryVariables,
  AntibodyTagSchemaQuery_bioentity_BasicFolderItemNode as AntibodyTagSchemaQueryBFINode,
} from 'antibodies/actions/__generated/AntibodyTagSchemaQuery';
import {ANTIBODY_TAG_SCHEMA_QUERY, onUpdateAntibodyVersion} from 'antibodies/actions/AntibodyActionUtil';
import AntibodyDomainInputs, {DomainWithProtein} from 'antibodies/actions/AntibodyDomainInputs';
import {editAntibodyStructure} from 'antibodies/AntibodyDAO';
import {AntibodyWithVersion} from 'antibodies/AntibodyTypedefs';
import {distinctDomainToKey, formatToSortedDistinctDomains} from 'antibodies/AntibodyUtils';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import Modal from 'ui-library/modals/Modal';

interface AntibodyEditModalProps {
  antibody: AntibodyWithVersion;
  onRequestClose: () => void;
}

/**
 * Modal for editing the domains of an antibody entity.
 */
export default function AntibodyEditModal({antibody, onRequestClose}: AntibodyEditModalProps): JSX.Element {
  const [saving, setSaving] = useState(false);
  const [domainsWithProteinByKey, setDomainsWithProteinByKey] = useState<Record<string, DomainWithProtein>>(
    Object.fromEntries(
      antibody.version.domains.map((domain) => [
        distinctDomainToKey({type: domain.type, specificity: domain.specificity}),
        {
          ...domain,
          proteinId:
            domain.entity == null
              ? null
              : isInaccessible(domain.entity)
                ? // Still want to add the id to the map even if it's inaccessible.
                  domain.entity.invalidId
                : domain.entity.id,
        },
      ])
    )
  );
  const schemaQueryResult = useBenchlingQuery<AntibodyTagSchemaQuery, AntibodyTagSchemaQueryVariables>(
    ANTIBODY_TAG_SCHEMA_QUERY,
    {variables: {id: antibody.id}}
  );
  if (schemaQueryResult.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }
  const invalidSchema =
    schemaQueryResult.type === QueryResultType.ERROR ||
    schemaQueryResult.data.bioentity == null ||
    isInaccessible(schemaQueryResult.data.bioentity) ||
    schemaQueryResult.data.bioentity.__typename !== 'BasicFolderItemNode' ||
    schemaQueryResult.data.bioentity.tagSchema == null;

  const sortedDistinctDomains = formatToSortedDistinctDomains(antibody.format);
  const canSubmit =
    !invalidSchema &&
    !saving &&
    sortedDistinctDomains.every(
      (domain) => domainsWithProteinByKey[distinctDomainToKey(domain)].proteinId != null
    );

  const onRequestSubmit = async (): Promise<void> => {
    setSaving(true);
    const domains = Object.values(domainsWithProteinByKey).map((domainWithProtein) => ({
      type: domainWithProtein.type,
      specificity: domainWithProtein.specificity,
      aaSequenceId: domainWithProtein.proteinId!,
    }));
    try {
      const {versionId, tags} = await editAntibodyStructure(antibody.id, antibody.currentVersionId, {
        domains,
      });
      onUpdateAntibodyVersion(antibody.id, versionId, tags);
      onRequestClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      buttons={[{text: saving ? 'Saving...' : 'Save', submit: true, disabled: !canSubmit}]}
      expandHeight={true}
      large={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
      title="Edit antibody"
    >
      {invalidSchema ? (
        <InlineBanner bannerType={InlineBannerType.ERROR} text="Failed to load antibody data" />
      ) : (
        <AntibodyDomainInputs
          // The type assertions for schemaQueryResult are already validated by invalidSchema being false.
          antibodySchemaId={(schemaQueryResult.data.bioentity as AntibodyTagSchemaQueryBFINode).tagSchema!.id}
          disabled={saving}
          format={antibody.format}
          domainsWithProteinByKey={domainsWithProteinByKey}
          setDomainsWithProteinByKey={setDomainsWithProteinByKey}
        />
      )}
    </Modal>
  );
}
